// @ts-strict-ignore
/* eslint-disable max-len */
import React from 'react';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  Box,
  DatePicker,
  Icon,
  IconName,
} from '@usealloy/component-library';

import { StyledDatePickerWrapper, StyledErrorText } from './styles';

export const MIN_MAX_COMPARE_ERROR = 'Min Accepted Value must be earlier than Max Accepted Value';

export type DateRangeFieldProps = {
  control: Control<any>;
  errors: FieldErrors;
  isTimestamp?: boolean;
  disabled?: boolean;
};

const DateRangeField = ({
  control, errors, isTimestamp = false, disabled = false,
}: DateRangeFieldProps) => {
  const {
    validValueMin: validValueMinError,
    validValueMax: validValueMaxError,
  } = errors;

  return (
    <Box direction="column" style={{ flex: '1 1 200%' }} gap={2}>
      <Box>
        <Controller
          control={control}
          name="validValueMin"
          defaultValue=""
          rules={{
            validate: (value, { validValueMax }) => {
              if (
                (value && validValueMax)
                  && !dayjs(value).isSameOrBefore(dayjs(validValueMax))
              ) {
                return MIN_MAX_COMPARE_ERROR;
              }
              return null;
            },
            deps: ['validValueMax', 'defaultValue'],
          }}
          render={({
            field: {
              value, onChange, ref: fieldRef, ...field
            },
          }) => {
            const dateFormat = isTimestamp
              ? 'YYYY-MM-DDTHH:mm:ss[Z]'
              : 'YYYY-MM-DD';

            return (
              <StyledDatePickerWrapper>
                <DatePicker
                  {...field}
                  ref={fieldRef}
                  label="Min Accepted Value"
                  data-testid="valid-value-min-date-picker"
                  value={value ? dayjs(value, dateFormat) : null}
                  showTime={isTimestamp}
                  onChange={(date: Dayjs) => {
                    onChange(date.format(dateFormat));
                  }}
                  style={{ width: '100%' }}
                  status={(validValueMinError || validValueMaxError) ? 'error' : ''}
                  disabled={disabled}
                />
              </StyledDatePickerWrapper>
            );
          }}
        />
        <Box
          direction="column"
          style={{
            width: 'var(--cl-space-5)',
            paddingTop: 'var(--cl-space-5)',
          }}
        >
          <Icon icon={IconName.Minus} />
        </Box>
        <Controller
          control={control}
          name="validValueMax"
          defaultValue=""
          rules={{
            validate: (value, { validValueMin }) => {
              if (
                (value && validValueMin)
                  && !dayjs(value).isSameOrAfter(dayjs(validValueMin))
              ) {
                return MIN_MAX_COMPARE_ERROR;
              }
              return null;
            },
            deps: ['validValueMin', 'defaultValue'],
          }}
          render={({
            field: {
              value, onChange, ref: fieldRef, ...field
            },
          }) => {
            const dateFormat = isTimestamp
              ? 'YYYY-MM-DDTHH:mm:ss[Z]'
              : 'YYYY-MM-DD';

            return (
              <StyledDatePickerWrapper>
                <DatePicker
                  {...field}
                  ref={fieldRef}
                  label="Max Accepted Value"
                  data-testid="valid-value-max-date-picker"
                  value={value ? dayjs(value, dateFormat) : null}
                  showTime={isTimestamp}
                  onChange={(date: Dayjs) => {
                    onChange(date.format(dateFormat));
                  }}
                  style={{ width: '100%' }}
                  status={(validValueMinError || validValueMaxError) ? 'error' : ''}
                  disabled={disabled}
                />
              </StyledDatePickerWrapper>
            );
          }}
        />
      </Box>
      {(validValueMinError || validValueMaxError) && (
        <Box justifyContent="flex-start">
          <StyledErrorText>
            {validValueMinError?.message as unknown as React.ReactNode || validValueMaxError?.message as unknown as React.ReactNode}
          </StyledErrorText>
        </Box>
      )}
    </Box>
  );
};

export default DateRangeField;
